import React, {useCallback, useEffect, useState, useMemo} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import * as AssetAction from '../redux/AssetAction'
import {Link} from 'react-router-dom'
import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import {AssetDelete} from '../components/AssetDelete'
import {
  AssetModel,
  AssetInitValues,
  AssetTableModelInitValues,
  AssetSearchModelInitValues,
} from '../models/AssetModel'
import {formatMoney} from '../../../helper'
import {Route, Breadcrumbs} from '../../../components/Breadcrumbs'
import {Can} from '../../../rbac/Can'
import {Table} from '../../../components/ReactTable'
import {formatDateTime} from '../../../helper/index'
import {Field, Form, Formik} from 'formik'
import * as Yup from 'yup'
import 'rsuite/dist/rsuite.css'

type Props = {
  className: string
}

export const defaultColDef = {
  resizable: true,
  initialWidth: 100,
  filter: true,
  floatingFilter: true,
  suppressMovable: true,
  suppressKeyboardEvent: (params: any) => params.editing,
}

export const Assets: React.FC<Props> = ({className}) => {
  const dispatch = useDispatch()

  const assets: any = useSelector<RootState>(({assets}) => assets.assets, shallowEqual)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [assetToDelete, setAssetToDelete] = useState<AssetModel>(AssetInitValues)
  const loading: any = useSelector<RootState>(({assets}) => assets.loading, shallowEqual)
  const pages: any = useSelector<RootState>(({assets}) => assets.pagination?.pages, shallowEqual)

  const [searchParams, setSearchParams]: any = useState({})
  const [show, setShow] = useState(false)
  const filterHandel = () => {
    setShow(!show)
  }

  useEffect(() => {
    dispatch(AssetAction.fetchAll(AssetTableModelInitValues))
  }, [dispatch])

  const fetchData = useCallback(
    ({pageSize, pageIndex, params, sortBy}: any) => {
      const queryParams = {
        page: pageIndex + 1,
        sortBy: sortBy[0]?.id || 'createdAt',
        sortOrder: sortBy[0]?.desc === false ? 'asc' : 'desc',
        search: params,
      }
      dispatch(AssetAction.fetchAll(queryParams))
    },
    [searchParams]
  )
  const columns = useMemo(
    () => [
      {
        Header: 'Created Date',
        accessor: 'createdAt',
        Cell: (cellInfo: any) => {
          if (cellInfo?.row?.original?.createdAt) {
            return formatDateTime(cellInfo?.row?.original?.createdAt)
          } else {
            return '-'
          }
        },
        show: true,
      },
      {
        Header: 'Asset',
        accessor: 'number',
        cellStyle: {textAlign: 'left'},
        show: true,
        Cell: (params: any) => (
          <Link to={`/assets/${params?.row?.original?._id}`}>{params?.row?.original?.number}</Link>
        ),
      },
      {Header: 'Goods Received Note', accessor: 'grn', show: true, disableSortBy: true},
      {
        Header: 'Product ',
        accessor: 'product.name',
        disableSortBy: true,
        show: true,
      },
      {Header: 'UOM', accessor: 'product.UOM', disableSortBy: true, show: true},
      {Header: 'Quantity', accessor: 'quantity', show: true},
      {Header: 'Location', accessor: 'location.name', disableSortBy: true, show: true},
      {Header: 'Disposal', accessor: 'disposal', show: true},
      {
        Header: 'Market Value',
        accessor: 'marketValue',
        Cell: (params: any) => {
          if (params?.row?.original?.marketValue) {
            return formatMoney(params?.row?.original?.marketValue)
          } else return '-'
        },
        show: true,
      },
      {
        Header: 'Actions',
        Cell: (params: any) => (
          <div className='action-btn-wrapper d-flex flex-shrink-0'>
            <Can do='update' on='assets'>
              <Link
                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                to={`/assets/${params?.row?.original?._id}/edit`}
                title='Edit Asset'
              >
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm m-0'
                  data-bs-toggle='modal'
                  data-bs-target='#kt_modal_delete_app'
                  title='Edit Asset'
                >
                  <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                </button>
              </Link>
            </Can>
            <Can do='delete' on='assets'>
              <button
                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                data-bs-toggle='modal'
                data-bs-target='#kt_modal_delete_app'
                onClick={() => {
                  setAssetToDelete(params?.row?.original)
                  setShowDeleteDialog(true)
                }}
                title='Delete Asset'
              >
                <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
              </button>
            </Can>
          </div>
        ),
      },
    ],
    []
  )

  const routes: Route[] = [
    {name: 'Dashboard', url: '/dashboard'},
    {name: 'Assets List', url: '/assets'},
  ]

  return (
    <>
      <AssetDelete
        show={showDeleteDialog}
        handleClose={() => {
          setShowDeleteDialog(false)
        }}
        asset={assetToDelete}
      />
      <div className={`card ${className}`}>
        <Breadcrumbs routes={routes} />
        <div className='card-header border-0 pt-5'>
          <h1 className='card-title'>
            <span className='card-label fw-bolder fs-3 mb-1'>Assets List</span>
          </h1>
          <Can do='create' on='assets'>
            <div
              className='card-toolbar'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              data-bs-trigger='hover'
              title='Add Asset'
            >
              <Link to='/assets/add' className='btn btn-sm btn-light-primary'>
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                Add Asset
              </Link>
            </div>
          </Can>
        </div>
        <div className='card-body'>
          <div
            className={`d-flex align-items-center ${
              !show ? 'justify-content-end' : 'justify-content-between'
            }`}
          >
            <p className='my-0 me-3' style={{color: '#004B87', fontSize: '14px'}}>
              <b>Search Filters</b>
            </p>
            <span onClick={filterHandel}>
              <img src='/media/svg/icons/General/filter.svg' alt='' />
            </span>
          </div>
          {show ? (
            <Formik
              initialValues={AssetSearchModelInitValues}
              validationSchema={Yup.object({
                number: Yup.string(),
                quantity: Yup.string(),
                disposal: Yup.string(),
                marketValue: Yup.string(),
              })}
              onSubmit={(values: any) => {
                if (values.disposal === 'Disposal Type') {
                  values.disposal = ''
                }
                setSearchParams(values)
              }}
            >
              {({values, setFieldValue}) => (
                <Form>
                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5'>
                    <div className='form-group col'>
                      <label htmlFor='number' className='fw-bold'>
                        Asset
                      </label>
                      <Field
                        name='number'
                        placeholder='Asset Number'
                        className='form-control form-control-lg'
                      />
                    </div>
                    <div className='form-group col'>
                      <label htmlFor='quantity' className='fw-bold'>
                        Quantity
                      </label>
                      <Field
                        name='quantity'
                        placeholder='Quantity'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>

                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5 mt-1'>
                    <div className='form-group col'>
                      <label htmlFor='disposal' className='fw-bold'>
                        Disposal Type
                      </label>
                      <Field
                        as='select'
                        className='form-select form-select-md'
                        data-control='select2'
                        name='disposal'
                      >
                        <option>Disposal Type</option>
                        <option value='YES'>YES</option>
                        <option value='NO'>NO</option>
                      </Field>
                    </div>
                    <div className='form-group col'>
                      <label htmlFor='marketValue' className='fw-bold'>
                        Market Value
                      </label>
                      <Field
                        name='marketValue'
                        placeholder='Market Value'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>

                  <div className='d-flex justify-content-end mt-10'>
                    <Link
                      to='/assets'
                      className='btn btn-sm btn-light-primary me-3'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Cancel'
                      onClick={() => {
                        setSearchParams(AssetSearchModelInitValues)
                        setFieldValue('number', '')
                        setFieldValue('disposal', '')
                      }}
                    >
                      <span className='indicator-label'>Cancel</span>
                    </Link>

                    <button
                      type='submit'
                      className='btn btn-sm btn-primary'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Search'
                      id='liveToastBtn'
                    >
                      <span className='indicator-label'>Search</span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          ) : null}
          <div className='d-flex mt-5 '>
            <div className='react-table-wrapper' style={{width: '100%'}}>
              <Table
                columns={columns}
                data={assets}
                params={searchParams}
                pageCount={pages}
                fetchData={fetchData}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Assets
